import React, { useCallback, useRef, useEffect } from 'react';
import {
  Typography,
  Grid,
  Box,
  IconButton,
  Button,
  useTheme,
} from '@mui/material';
import Editor from '@monaco-editor/react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useOrganization from '../../hooks/UseOrganization';
import utils from '../../utils/Utils';
import SwalToast from '../../components/Common/SwalToast';

const FileConfig = () => {
  const monacoRef = useRef(null);
  const themes = useTheme();
  const { getFileConfig, editFileConfig, getFileDBConfig } = useOrganization();
  const userTheme = useSelector((reduxState) => reduxState.common.userTheme);
  const { state: locationState } = useLocation();
  const activatedOrgName = useSelector((state) => state.common.orgName);
  const { orgName, productDetails } = locationState || {};
  const navigate = useNavigate();

  const [code, setCode] = React.useState(`{ "key": "value"}`);

  useEffect(() => {
    if (activatedOrgName !== orgName) {
      navigate('/application-setting');
    }
    getFileConfig(productDetails?.name, orgName)
      .then((res) => {
        if (res?.data) {
          setCode(JSON.stringify(res.data, null, 2));
        }
      })
      .catch((error) => {
        console.error('Error fetching file config:', error);
      });
  }, [orgName, activatedOrgName]);

  const handleFileConfigSave = useCallback(() => {
    let parsedData;
    try {
      parsedData = JSON.parse(code); // Try parsing JSON
    } catch (err) {
      console.error('Invalid JSON:', err);
      SwalToast({
        icon: 'warning',
        title: 'Invalid JSON format. Please fix the syntax before saving.',
      });
      return; // Stop execution if invalid
    }
    const payload = {
      product: productDetails?.name,
      data: parsedData,
    };
    editFileConfig(payload, orgName).then((res) => {
      setCode(JSON.stringify(res.data, null, 2));
    });
  }, [code, editFileConfig, orgName, productDetails?.name]);

  const handleReset = React.useCallback(() => {
    getFileDBConfig(productDetails?.id, orgName, false).then(() => {
      getFileConfig(productDetails?.name, orgName, false).then((res) => {
        if (res?.data) {
          setCode(JSON.stringify(res.data, null, 2));
        }
      });
    });
  }, [getFileDBConfig, productDetails, orgName]);

  const handleEditorDidMount = useCallback((monaco) => {
    monacoRef.current = monaco;
    utils.applyThemeToMonacoEditor(monaco, themes, userTheme);

    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: true,
    });
  }, []);

  const handleGoBack = useCallback(() => {
    window.history.back();
  }, []);

  return (
    <Box display="flex" flexDirection="column" margin="20px">
      <Box>
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            component="ol"
            sx={{
              listStyleType: 'none',
              padding: '0px',
              margin: '0px',
            }}
          >
            <IconButton
              onClick={handleGoBack}
              title="Go back"
              aria-label="Back"
              color="primary"
            >
              <ArrowBackIcon aria-label="Go Back" />
            </IconButton>
            <Typography variant="subtitle2" component="li">
              Application
            </Typography>

            <ChevronRightIcon />
            <Typography variant="subtitle2" component="li">
              {productDetails?.name}
            </Typography>

            <ChevronRightIcon />
            <Typography variant="subtitle1" component="li">
              File configuration
            </Typography>
          </Box>
        </Grid>
      </Box>
      <Grid
        item
        xs={12}
        sx={{
          '& .lines-content.monaco-editor-background': {
            paddingLeft: '24px !important',
          },
        }}
        mt={1}
        mb={4}
      >
        <Editor
          height="calc(100vh - 200px)"
          defaultLanguage="json"
          value={code}
          theme={
            themes.palette.mode === 'dark' ? 'myThemeDark' : 'myThemeLight'
          }
          beforeMount={handleEditorDidMount}
          options={{
            fontSize: 16,
            lineHeight: 22,
            fontFamily: 'Open Sans',
            lineDecorationsWidth: 16,
            lineNumbersMinChars: 3,
            glyphMargin: true,
            padding: {
              top: 12,
              bottom: 12,
              left: 12,
              right: 12,
            },
            scrollbar: {
              vertical: 'auto',
              horizontal: 'auto',
              verticalScrollbarSize: 5,
              horizontalScrollbarSize: 5,
            },
          }}
          onChange={(value) => setCode(value)}
          editorDidMount={(editor) => {
            setTimeout(() => {
              editor.getAction('editor.action.formatDocument').run();
            }, 300);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Box
          height="60px"
          backgroundColor={themes.palette.background.paper}
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          position="fixed"
          zIndex={100}
          bottom={0}
          left="17%"
          width="83%"
        >
          <Box
            pr="12px"
            display="flex"
            justifyContent="flex-end"
            gap={2}
            mr={2}
          >
            <Button
              color="primary"
              variant="outlined"
              size="large"
              onClick={handleReset}
              title="Reset"
              fullWidth={true}
            >
              Reset
            </Button>
            <Button
              color="primary"
              variant="contained"
              size="large"
              onClick={handleFileConfigSave}
              title="Save"
              fullWidth={true}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Grid>
    </Box>
  );
};

export default FileConfig;
